import PropTypes from "prop-types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSliders, faGrip, faMapMarkerAlt, faChevronUp, faBullseye, faTimes,
} from "@fortawesome/free-solid-svg-icons";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import "../../style/MarketplaceFilterBar.css";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function MarketplaceFilterBar({
  sortBy,
  onSortChange,
  onTogglePanel,
  isPanelOpen,
  activeFilterCount,
  viewMode,
  onViewModeChange,
  darkMode,
  resultsCount,
  resultsLabel,
  lastFetched,
  onRefresh,
  isRefreshing,
  radiusKm,
  showRadius,
  onToggleRadius,
  isDetailOpen,
  filters,
  onRemoveFilter,
}) {
  const { t } = useTranslation();
  const themeClass = darkMode ? "mfb--dark" : "mfb--light";

  // build chips as {label, keys} where keys are filter fields to clear on ×
  const chips = useMemo(() => {
    if (!filters) return [];
    const c = [];

    // categories
    if (filters.categories?.length) {
      const label = filters.categories.length === 1
        ? filters.categories[0]
        : `${filters.categories.length} categories`;
      c.push({ label, keys: ["categories"] });
    }

    // materials
    if (filters.materials?.length) {
      const label = filters.materials.length === 1
        ? filters.materials[0]
        : `${filters.materials.length} materials`;
      c.push({ label, keys: ["materials"] });
    }

    // cost range
    const cMin = filters.costMin?.toString().trim();
    const cMax = filters.costMax?.toString().trim();
    if (cMin || cMax) {
      const lo = cMin ? `$${cMin}` : "$0";
      const hi = cMax ? `$${cMax}` : "∞";
      c.push({ label: `${lo}–${hi}/lb`, keys: ["costMin", "costMax"] });
    }

    // quantity range
    const qMin = filters.quantityMin?.toString().trim();
    const qMax = filters.quantityMax?.toString().trim();
    if (qMin || qMax) {
      const lo = qMin || "0";
      const hi = qMax || "∞";
      c.push({ label: `${lo}–${hi} lbs`, keys: ["quantityMin", "quantityMax"] });
    }

    // pickup time range
    const tFrom = filters.pickupTimeFrom?.toString().trim();
    const tTo = filters.pickupTimeTo?.toString().trim();
    if (tFrom || tTo) {
      const lo = tFrom || "09:00";
      const hi = tTo || "17:00";
      c.push({ label: `${lo}–${hi}`, keys: ["pickupTimeFrom", "pickupTimeTo"] });
    }

    // latest pickup by — include year
    if (filters.pickupLatestBy?.trim()) {
      const d = filters.pickupLatestBy.trim();
      try {
        const [y, m, day] = d.split("-");
        const dt = new Date(Number(y), Number(m) - 1, Number(day));
        c.push({ label: `by ${dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`, keys: ["pickupLatestBy"] });
      } catch { c.push({ label: `by ${d}`, keys: ["pickupLatestBy"] }); }
    }

    // weekdays
    if (Array.isArray(filters.weekdays) && filters.weekdays.length > 0) {
      const sorted = [...filters.weekdays].sort((a, b) => Number(a) - Number(b));
      c.push({ label: sorted.map((d) => DAY_LABELS[Number(d)] || d).join(", "), keys: ["weekdays"] });
    }

    // delivery method — only show chip when one is selected (both = default, no chip)
    if (Array.isArray(filters.deliveryMethods) && filters.deliveryMethods.length === 1) {
      const m = filters.deliveryMethods[0];
      c.push({ label: m === "delivery" ? "Delivery only" : "Pickup only", keys: ["deliveryMethods"] });
    }

    // delivery method
    if (filters.deliveryMethod) {
      c.push({ label: filters.deliveryMethod === "delivery" ? "Delivery" : "Pickup", keys: ["deliveryMethod"] });
    }

    return c;
  }, [filters]);

  return (
    <div className={`mfb-wrap ${themeClass}`}>
      <div className="mfb__left">
        <button
          className={`mfb__filters-btn ${isPanelOpen ? "mfb__filters-btn--active" : ""}`}
          onClick={onTogglePanel}
        >
          <FontAwesomeIcon icon={faSliders} />
          <span>{t("All Filters")}</span>
          {activeFilterCount > 0 && (
            <span className="mfb__badge">{activeFilterCount}</span>
          )}
          <FontAwesomeIcon
            icon={faChevronUp}
            className={`mfb__chevron ${isPanelOpen ? "mfb__chevron--open" : ""}`}
          />
        </button>

        <select
          className="mfb__select"
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
        >
          <option value="newest">{t("Newest")}</option>
          <option value="cost_asc">{t("Price: Low → High")}</option>
          <option value="cost_desc">{t("Price: High → Low")}</option>
          <option value="quantity_desc">{t("Most Available")}</option>
        </select>

        <div className="mfb__toggle">
          <button
            className={`mfb__toggle-btn ${viewMode === "grid" ? "mfb__toggle-btn--active" : ""}`}
            onClick={() => onViewModeChange("grid")}
            title={t("Grid view")}
          >
            <FontAwesomeIcon icon={faGrip} />
          </button>
          <button
            className={`mfb__toggle-btn ${viewMode === "map" ? "mfb__toggle-btn--active" : ""}`}
            onClick={() => onViewModeChange("map")}
            title={t("Map view")}
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </button>
        </div>

        {viewMode === "map" && onToggleRadius && (
          <button
            className={`mfb__radius-btn ${showRadius ? "mfb__radius-btn--active" : ""}`}
            onClick={onToggleRadius}
            title={t("Toggle search radius")}
          >
            <FontAwesomeIcon icon={faBullseye} />
            <span>{radiusKm} km</span>
          </button>
        )}

        {chips.length > 0 && (
          <div className="mfb__chips">
            {chips.map((chip, i) => (
              <span key={i} className="mfb__chip mfb__chip--active">
                {chip.label}
                {onRemoveFilter && (
                  <FontAwesomeIcon
                    icon={faTimes}
                    className="mfb__chip-x"
                    onClick={(e) => { e.stopPropagation(); onRemoveFilter(chip.keys); }}
                  />
                )}
              </span>
            ))}
          </div>
        )}

        {resultsLabel && (
          <span className="mm-results-inline">{resultsLabel}</span>
        )}
      </div>

      <div className={`mfb__right ${isDetailOpen ? "mfb__right--over-panel" : ""}`}>
        <LastUpdatedBadge timestamp={lastFetched} onRefresh={onRefresh} isRefreshing={isRefreshing} variant="receiver" />
      </div>
    </div>
  );
}

MarketplaceFilterBar.propTypes = {
  sortBy: PropTypes.string,
  onSortChange: PropTypes.func,
  onTogglePanel: PropTypes.func,
  isPanelOpen: PropTypes.bool,
  activeFilterCount: PropTypes.number,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  darkMode: PropTypes.bool,
  resultsCount: PropTypes.number,
  resultsLabel: PropTypes.string,
  lastFetched: PropTypes.number,
  onRefresh: PropTypes.func,
  isRefreshing: PropTypes.bool,
  radiusKm: PropTypes.number,
  showRadius: PropTypes.bool,
  onToggleRadius: PropTypes.func,
  isDetailOpen: PropTypes.bool,
  filters: PropTypes.object,
  onRemoveFilter: PropTypes.func,
};

export default MarketplaceFilterBar;
